import type { CardElementProps, ElementProps } from '@stripe/react-stripe-js';
import type { StripeElementChangeEvent } from '@stripe/stripe-js';
import type { TextInputProps } from '../TextInput/index.ts';
import { useCallback, useState } from 'react';

interface UseStripeTextInputOptions extends Pick<TextInputProps, 'disabled' | 'error' | 'name'> {
  onBlur?: (event: { target: { name?: string } }) => void;
  onChange?: (event: {
    target: { name?: string; value: StripeElementChangeEvent['complete'] };
  }) => void;
}

export function useStripeElementContainer({
  disabled,
  error,
  name,
  onBlur,
  onChange
}: UseStripeTextInputOptions) {
  const [internalError, setInternalError] = useState<string | undefined>();
  const [focused, setFocused] = useState<boolean>(false);

  const handleChange = useCallback(
    (event: StripeElementChangeEvent) => {
      const errorMessage = typeof event.error !== 'undefined' ? event.error.message : undefined;

      setInternalError(errorMessage);

      onChange?.({ target: { name, value: event.complete } });
    },
    [name, onChange]
  );

  const handleBlur = useCallback(() => {
    setFocused(false);
    onBlur?.({ target: { name } });
  }, [name, onBlur]);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const stripeElementProps: CardElementProps & ElementProps = {
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    options: {
      disabled,
      style: {
        base: {
          fontFamily:
            '"Inter var", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          fontSize: '14px'
        },
        invalid: {
          color: '#7f1d1d'
        }
      }
    }
  };

  return {
    error: internalError || error,
    isFocused: focused,
    stripeElementProps
  };
}
